import React, { useEffect, useId, useRef } from "react";
import "./Characters.css";

/**
 * HostCharacter
 * -------------
 * Robot host overlay that displays contextual help/instructions.
 *
 * Behaviour:
 * - Controlled by `isHelpVisible`.
 * - Dismiss via button, Escape, or (optionally) backdrop click.
 */

type Props = {
  isHelpVisible: boolean;
  helpContent: React.ReactNode;
  closeHelpContent: () => void;
  buttonLabel?: string;
  closeOnBackdropClick?: boolean;
  /** Optional second action (e.g. a "Leave"/destructive choice alongside the primary button). */
  secondaryButtonLabel?: string;
  onSecondaryAction?: () => void;
};

const HostCharacter: React.FC<Props> = ({
  isHelpVisible,
  helpContent,
  closeHelpContent,
  buttonLabel = "Close",
  closeOnBackdropClick = true,
  secondaryButtonLabel,
  onSecondaryAction,
}) => {
  /** REFS ---------------------------------------------------------- */
  const primaryBtnRef = useRef<HTMLButtonElement | null>(null);

  /** IDS ----------------------------------------------------------- */
  const titleId = useId();
  const descId = useId();

  /** EFFECTS ------------------------------------------------------- */
  // Focus the primary action when becoming visible
  useEffect(() => {
    if (!isHelpVisible) return;
    primaryBtnRef.current?.focus();
  }, [isHelpVisible]);

  // Escape-to-close only while visible
  useEffect(() => {
    if (!isHelpVisible) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeHelpContent();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isHelpVisible, closeHelpContent]);

  /** HANDLERS ------------------------------------------------------ */
  const handleBackdropClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!closeOnBackdropClick) return;
    if (e.currentTarget === e.target) closeHelpContent();
  };

  /** RENDER -------------------------------------------------------- */
  if (!isHelpVisible) return null;

  return (
    <div
      className="host-overlay"
      onClick={handleBackdropClick}
      aria-hidden={false}
    >
      <div
        id="hostCharacter"
        className="robot host-appear"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        onClick={(e) => e.stopPropagation()}
      >
        <video
          className="robot-video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          src="/assets/video/Robot-Character-Introduction.webm"
        />

        <div className="speech-bubble">
          <h2 id={titleId} className="sr-only">
            Host message
          </h2>

          <div id={descId}><p>{helpContent}</p></div>

          <div className="host-actions">
            <button
              ref={primaryBtnRef}
              className="btn-blue"
              onClick={closeHelpContent}
              type="button"
            >
              {buttonLabel}
            </button>
            {secondaryButtonLabel && onSecondaryAction && (
              <button
                className="btn-host-secondary"
                onClick={onSecondaryAction}
                type="button"
              >
                {secondaryButtonLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostCharacter;
